<template>
  <div class="repair-page">
    <div class="repair-workspace">
      <block-buttons
        title="Ремонт устройств"
        description="Заказы, клиенты и ход работы — в одном месте"
        :actions="toolbarActions"
      >
        <template #icon>
          <Wrench :size="22" />
        </template>
      </block-buttons>

      <repair-summary
        :data="data"
        :unavailable="loading"
      />

      <data-table
        :rows="filteredData"
        :columns="tableColumns"
        :loading="loading"
        :search-keys="searchKeys"
        title="Заказы на ремонт"
        description="Следите за статусами и управляйте каждым заказом"
        search-placeholder="Клиент, номер телефона или устройство"
        empty-text="Нет заказов с выбранным статусом"
        @retry="loadRepairs(true)"
      >
        <template #header>
          <span class="repair-workspace__count">
            {{ data.length }} заказов
          </span>
        </template>

        <template #filters>
          <label class="repair-workspace__filter">
            Статус
            <select v-model="status">
              <option value="">Все статусы</option>

              <option
                v-for="item in getStatuses"
                :key="item.code"
                :value="item.code"
              >
                {{ item.name }}
              </option>
            </select>
          </label>
        </template>

        <template #cell-status="{ row }">
          <span
            class="repair-workspace__status"
            :class="`repair-workspace__status--${row.repairStatus}`"
          >
            <i />
            {{ row.status }}
          </span>
        </template>

        <template #cell-client="{ row }">
          <strong>{{ row.client || 'Клиент не указан' }}</strong>
          <a
            v-if="row.clientPhoneNumber"
            :href="`tel:${phoneHref(row.clientPhoneNumber)}`"
          >
            {{ row.clientPhoneNumber }}
          </a>
          <small v-else>Телефон не указан</small>
        </template>

        <template #cell-deviceName="{ row }">
          <strong>{{ row.deviceName || 'Устройство не указано' }}</strong>
          <small>{{ row.deviceColor || 'Цвет не указан' }}</small>
        </template>

        <template #cell-createdAt="{ row }">
          <span class="repair-workspace__date">{{ row.createdAt ? row.createdAtFormatted : '—' }}</span>
          <small>Заказ № {{ row.id }}</small>
        </template>

        <template #cell-repairPrice="{ row }">
          <strong class="repair-workspace__price">
            {{ row.repairPrice == null ? 'Не указана' : priceToFormat(row.repairPrice) }}
          </strong>
        </template>

        <template #cell-actions="{ row }">
          <select
            v-if="rowActions(row).length"
            class="repair-workspace__actions"
            :disabled="loading || getIsOffline"
            :aria-label="`Действия с заказом ${row.id}`"
            @change="selectAction($event, row)"
          >
            <option value="">Действия</option>

            <option
              v-for="action in rowActions(row)"
              :key="action.id"
              :value="action.id"
            >
              {{ action.label }}
            </option>
          </select>

          <span v-else>—</span>
        </template>
      </data-table>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import {
  Wrench,
  Plus,
  Calculator,
  RefreshCw
} from 'lucide-vue'

import { priceToFormat } from '@/helper'

import BlockButtons from '@/components/blockButtons'
import RepairSummary from '@/components/repairSummary'

import DataTable from '@/components/dataTable'

import permissions from '@/mixins/permissions'

export default {
  name: 'RepairV2',
  components: {
    Wrench,
    BlockButtons,
    DataTable,
    RepairSummary
  },
  mixins: [permissions],
  data () {
    return {
      loading: false,
      status: '',
      dialog: null,
      searchKeys: [
        'client',
        'clientPhoneNumber',
        'deviceName',
        'deviceColor',
        'createdAtFormatted',
        'status'
      ]
    }
  },
  computed: {
    ...mapGetters('repair', { repairData: 'getData' }),
    ...mapGetters('config', ['getIsOffline']),
    ...mapGetters('settings', ['getTableColumns', 'getDefaultValues']),
    toolbarActions () {
      const actions = [
        {
          id: 'refresh',
          label: 'Обновить',
          icon: RefreshCw,
          disabled: this.loading,
          handler: () => this.loadRepairs(true)
        },
        {
          id: 'calculator',
          label: 'Калькулятор ремонта',
          icon: Calculator,
          handler: () => this.openModal({ name: 'repair-calculator' })
        }
      ]

      if (this.isAllowedOperation('create', 'repair')) {
        actions.push({
          id: 'create',
          label: 'Новый ремонт',
          icon: Plus,
          primary: true,
          disabled: this.loading || this.getIsOffline,
          handler: () => {
            this.openModal({
              name: 'create-base',
              propsData: {
                name: 'repair',
                moduleAction: 'repair',
                type: 'create',
                title: 'Создание нового ремонта',
                text: 'Укажите данные клиента, устройства и ремонта.',
                textCancelBtn: 'Закрыть',
                textConfirmBtn: 'Создать ремонт'
              }
            })
          }
        })
      }

      return actions
    },
    tableColumns () {
      return [
        {
          key: 'actions',
          label: 'Действия',
          sortable: false
        },
        ...(this.getTableColumns?.['repair'] || [])
      ]
    },
    getStatuses () {
      return this.getDefaultValues?.['repair-statuses'] || []
    },
    data () {
      return this.repairData.map(item => ({
        ...item,
        client: [item.clientName, item.clientSurname].filter(Boolean).join(' '),
        status: this.getStatuses.find(({ code }) => code === item.repairStatus)?.name || 'Не указан'
      }))
    },
    filteredData () {
      return this.status
        ? this.data.filter(({ repairStatus }) => repairStatus === this.status)
        : this.data
    }
  },
  mounted () {
    this.loadRepairs()
  },
  methods: {
    ...mapActions('repair', ['loadData', 'remove', 'inRepair', 'close']),
    ...mapActions('modal', ['openModal']),
    priceToFormat,
    async loadRepairs (force = false) {
      if (this.loading) {
        return
      }

      this.$bus.emit('start-loader')
      this.loading = true

      await this.loadData({ force })

      this.loading = false
      this.$bus.emit('stop-loader')
    },
    phoneHref (value) {
      return String(value).replace(/[^+\d]/g, '')
    },
    rowActions (row) {
      const isRepairNotClosed = row.repairStatus !== 'closed'

      return [
        {
          id: 'info',
          label: 'Информация',
          visible: true
        },
        {
          id: 'change',
          label: 'Редактировать',
          visible: this.isAllowedOperation('change', 'repair')
        },
        {
          id: 'inRepair',
          label: 'Взять в ремонт',
          visible: isRepairNotClosed && row.repairStatus !== 'repairInRepair'
        },
        {
          id: 'close',
          label: 'Завершить ремонт',
          visible: isRepairNotClosed
        },
        {
          id: 'expense',
          label: 'Добавить расходы',
          visible: isRepairNotClosed
        },
        {
          id: 'remove',
          label: 'Удалить',
          visible: this.isAllowedOperation('remove', 'repair')
        }
      ].filter(({ visible }) => visible)
    },
    selectAction (event, row) {
      const action = event.target.value

      event.target.value = ''

      if (!action) {
        return
      }

      this.openAction(action, row)
    },
    openAction (action, row) {
      switch (action) {
        case 'info':
          this.openModal({
            name: 'info-base',
            propsData: {
              name: 'repair',
              moduleAction: 'repair',
              type: 'info',
              title: `Информация о ремонте - ${row.client}`,
              textCancelBtn: 'Закрыть',
              onClose: () => this.loadRepairs(true),
              data: row
            }
          })

          break
        case 'change':
          this.openModal({
            name: 'change-base',
            propsData: {
              name: 'repair',
              moduleAction: 'repair',
              type: 'change',
              title: `Редактирование ремонта - ${row.client}`,
              text: 'Измените необходимые данные',
              textCancelBtn: 'Закрыть',
              textConfirmBtn: 'Сохранить',
              data: row
            }
          })

          break
        case 'inRepair':
          this.inRepair(row.id)

          break
        case 'close':
          this.openModal({
            name: 'close-repair',
            propsData: {
              name: 'repair',
              moduleAction: 'repair',
              type: 'info',
              data: row
            }
          })

          break
        case 'expense':
          this.openModal({
            name: 'create-base',
            propsData: {
              name: 'repair-cash-operation',
              moduleAction: 'repairCashOperation',
              type: 'create',
              title: 'Создание расхода в ремонте',
              text: 'Укажите данные для расхода операции',
              textCancelBtn: 'Закрыть',
              textConfirmBtn: 'Создать',
              onClose: () => this.loadRepairs(true),
              data: {
                // Нужно для того чтоб кассовая операция для ремонта привязалась к самому ремонту
                repairId: row.id
              }
            }
          })

          break
        case 'remove':
          this.removeRepair(row)
      }
    },
    async removeRepair ({ id }) {
      const { error } = await this.remove(id)

      if (error) {
        this.$notify({
          type: 'error',
          title: 'Ошибка при удалении!',
          text: 'Произошла ошибка при удалении ремонта!',
          clean: false,
          duration: 3000
        })

        return
      }

      this.$notify({
        type: 'success',
        title: 'Ремонт успешно удален!',
        text: '',
        clean: false,
        duration: 3000
      })
    }
  }
}
</script>

<style scoped lang="scss" src="./style.scss" />
